import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, XCircle, ExternalLink, ArrowRight } from "lucide-react";
import PublicNav from "@/components/PublicNav";
import PublicFooter from "@/components/PublicFooter";
import Section from "@/components/Section";
import StandardSeal from "@/components/StandardSeal";
import api from "@/lib/api";

/**
 * /standard-verified/:slug — public verification landing page.
 *
 * When a badge appears on a 3rd-party site the linked URL brings the visitor
 * here.  We show the seal, the org name, the verified date, and a link back
 * to /standard.  If the org's approval has been revoked we say so clearly.
 */
export default function StandardVerifiedPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    api.get(`/standard-verified/${slug}`)
      .then((r) => setState({ loading: false, data: r.data, notFound: false }))
      .catch((e) => setState({ loading: false, notFound: e?.response?.status === 404 }));
  }, [slug]);

  if (state.loading) {
    return (
      <>
        <PublicNav />
        <Section testId="verify-loading">
          <div className="text-sm text-[color:var(--dt6-muted)]">Verifying badge…</div>
        </Section>
        <PublicFooter />
      </>
    );
  }

  if (state.notFound) {
    return (
      <>
        <PublicNav />
        <Section testId="verify-notfound">
          <div className="dt6-card p-10 text-center">
            <XCircle size={40} className="text-[color:var(--dt6-danger)] mx-auto mb-4" />
            <h1 className="font-heading text-3xl font-bold mb-2">Badge not recognised.</h1>
            <p className="text-sm text-[color:var(--dt6-muted)] mb-6">
              This badge slug does not appear in the DT6 registry.  It may be a copy-paste error,
              or the badge was never issued.
            </p>
            <Link to="/standard" className="dt6-btn dt6-btn-primary">
              Read The DT6 Standard <ArrowRight size={14} />
            </Link>
          </div>
        </Section>
        <PublicFooter />
      </>
    );
  }

  const { active, org_name, status, verified_at } = state.data;

  return (
    <>
      <PublicNav />
      <Section eyebrow={active ? "/ STANDARD-VERIFIED" : "/ APPROVAL REVOKED"} testId="verify-page">
        <div
          className="dt6-card p-10 flex flex-col md:flex-row items-center gap-8"
          style={{ borderColor: active ? "var(--dt6-primary)" : "var(--dt6-danger)" }}
        >
          <StandardSeal size={140} variant={active ? "primary" : "mono-parchment"} testId="verify-seal" />
          <div className="flex-1">
            {active ? (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 size={16} className="text-[color:var(--dt6-success)]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--dt6-success)]">Verified · Active</span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="verify-org-name">{org_name}</h1>
                <p className="text-sm text-[color:var(--dt6-muted)] mt-2">
                  Has passed the DT6 Standard and is authorised to use DT6 political data infrastructure.
                </p>
                {verified_at && (
                  <p className="text-xs font-mono text-[color:var(--dt6-muted)] mt-3" data-testid="verify-date">
                    / VERIFIED {verified_at.slice(0, 10)}
                  </p>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-2">
                  <XCircle size={16} className="text-[color:var(--dt6-danger)]" />
                  <span className="font-mono text-xs uppercase tracking-widest text-[color:var(--dt6-danger)]" data-testid="verify-status">
                    Approval {status}
                  </span>
                </div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold" data-testid="verify-org-name">{org_name}</h1>
                <p className="text-sm text-[color:var(--dt6-muted)] mt-2">
                  This organisation is no longer authorised to hold the DT6 Standard badge.
                  Any embedded badge on 3rd-party sites is no longer valid.
                </p>
              </>
            )}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link to="/standard" className="dt6-btn dt6-btn-primary" data-testid="verify-standard-link">
                Read The Standard <ArrowRight size={14} />
              </Link>
              <Link to="/company" className="dt6-btn" data-testid="verify-company-link">
                About DT6 <ExternalLink size={12} />
              </Link>
            </div>
          </div>
        </div>
      </Section>
      <PublicFooter />
    </>
  );
}
